import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AddOutlined, CloseOutlined, PauseCircleOutlineOutlined, RefreshOutlined, SearchOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Chip, IconButton, InputAdornment, Skeleton, TextField, Tooltip } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import StatusChip from "components/shared/StatusChip/StatusChip";
import { useCurrentUserRoleNames, usePermission } from "components/RequirePermission/useAccessPermissions";
import { useOrganizationCapabilities, useRoleAssignmentCapabilities } from "components/RequirePermission/useCapabilities";
import { useEffectiveOrgRole } from "components/RequirePermission/useEffectiveOrgRole";
import { isCrossOrgAdminRole } from "components/RequirePermission/RequireEcrOpsRole";
import { useGetRoleAssignmentsQuery, useGetRolesQuery } from "state/rtkQuery/access/accessQueries";
import { useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import {
  buildRolesById,
  getAssigneeDisplay,
  resolveAssignmentRole,
} from "utils/accessManagement/roleAssignmentDisplay";
import { canManageRole, isDefaultUserRole } from "utils/accessManagement/roleAssignmentForm";
import { useColumnsToolbar } from "../ColumnsToolbar/ColumnsToolbar";
import DeactivateOrgAssignmentDialog from "./DeactivateOrgAssignmentDialog";
import OrgAssignRoleDrawer from "./OrgAssignRoleDrawer";

// Org-scoped = grants at least one organization:*/user:* permission AND is
// not one of the 2 real cross-org-admin roles (EnterpriseAdmin/
// OperationsAdmin — see isCrossOrgAdminRole, the same role-name check used
// everywhere else in the app for this exact purpose), AND is not the default
// "User" role every account gets automatically (never something an admin
// assigns — see isDefaultUserRole). Every real role (including
// OrganizationOwner/OrganizationApprover/Leadership/OperationsUser — the
// latter two both per-org-assignable, not cross-org-admin — see
// isCrossOrgAdminRole) also carries a few baseline platform:read-type
// permissions that everyone gets — presence of ANY platform:* permission is
// NOT a useful signal for "is this a platform-wide admin role" and must not
// be used to exclude a role here (that was the earlier version of this
// check, and it excluded every real org role since none of them have zero
// platform:* permissions).
const PAGE_SIZE = 10;

const ASSIGNABLE_ORG_ROLE_NAMES = ["OrganizationApprover", "Leadership"];

const isOrgScopedRole = (role) => {
  if (isCrossOrgAdminRole([role.role_name])) return false;
  if (isDefaultUserRole(role.role_name)) return false;
  const permissions = role.permissions || [];
  return permissions.some((p) => p.startsWith("organization:") || p.startsWith("user:"));
};

// Lightweight, org-locked version of the global Role Assignments screen: org_id
// is fixed to the current org (never editable), the role picker is filtered to
// org-scoped roles only, and there is no cross-org Roles & Permissions or
// Effective Access here — those stay in the platform-wide ecr-ops surface.
const OrgAccessPage = () => {
  const { components: columnsToolbarComponents, ...columnsToolbarProps } = useColumnsToolbar();
  const { orgId } = useParams();
  const { data: orgData } = useGetOrgByIdQuery(orgId);
  // Real, always-fresh RBAC data (GET /user/organizations) instead of the old
  // state.auth.permissions populated once at login — plus the same global
  // edit-capability override RequireOrgOwnerRole uses, so e.g. EnterpriseAdmin
  // can manage org access here without needing an explicit per-org assignment.
  // hasGlobalEditAccess alone is not enough — see RequireOrgOwnerRole's
  // isCrossOrgAdminRole note: a per-org Owner assignment on a DIFFERENT org can
  // leak organization:id:update into the global permission union.
  // OperationsUser is org-scoped only (see isCrossOrgAdminRole, which
  // deliberately excludes it) but gets full OrganizationOwner-equivalent
  // write access on orgs it's actually assigned to — folded into isOwner,
  // same as isOrgOwner.
  const { isOwner: isOrgOwner, isOperationsUser: isOrgOperationsUser, role: myOrgRoleName } = useEffectiveOrgRole(orgId);
  const hasGlobalEditAccess = usePermission("organization:id:update");
  const roleNames = useCurrentUserRoleNames();
  const isOwner = isOrgOwner || isOrgOperationsUser || (hasGlobalEditAccess && isCrossOrgAdminRole(roleNames));

  // Assign/Deactivate need the specific auth:role-assignment:create/update
  // permission — useRoleAssignmentCapabilities is already scoped to THIS
  // org's role (see useEffectiveOrgPermission): it checks the role
  // useEffectiveOrgRole(orgId) reports for this org against THAT role's own
  // permissions, not the user's global cross-org union.
  const { canReadRoleAssignment, canCreateRoleAssignment, canUpdateRoleAssignment } =
    useRoleAssignmentCapabilities(orgId);
  const { canUpdateOrganizationById } = useOrganizationCapabilities(orgId);

  const [assignOpen, setAssignOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deactivateAssignment, setDeactivateAssignment] = useState(null);

  const { data: rolesData } = useGetRolesQuery({ perPage: 200 });
  const orgRoles = useMemo(() => (rolesData?.roles || []).filter(isOrgScopedRole), [rolesData]);
  const rolesById = useMemo(() => buildRolesById(rolesData?.roles), [rolesData]);

  // Role-priority hierarchy: you can only assign/deactivate a role at or
  // below your own highest-ranked role ON THIS ORG (not your global cross-org
  // max — see canManageRole's own comment) — e.g. an Approver can't grant
  // Owner-level access. myOrgRolePriority looks up the priority of whatever
  // role useEffectiveOrgRole(orgId) reports for THIS org specifically.
  //
  // The one deliberate exception: the 2 real cross-org-admin roles
  // (EnterpriseAdmin/OperationsAdmin — see isCrossOrgAdminRole) skip this
  // filter entirely rather than defaulting to priority 0, which would
  // incorrectly block them from assigning ANY role on an org where they hold
  // no per-org assignment (their access there comes from the global role
  // alone). This is the same unconditional bypass isCrossOrgAdminRole already
  // gets everywhere else in the app (e.g. isOwner above) — holding
  // OrganizationOwner on several DIFFERENT orgs does NOT qualify here; each of
  // those is a separate, ordinary per-org assignment subject to the normal
  // priority check on its own org. OperationsUser is deliberately NOT part of
  // this bypass (unlike isOwner above): once org-scoped, it has a real
  // per-org priority via myOrgRolePriority, same as any other per-org
  // assignee, and must go through the normal canManageRole check below.
  const isCrossOrgAdmin = isCrossOrgAdminRole(roleNames);
  const myOrgRolePriority = useMemo(() => {
    if (!myOrgRoleName || !rolesData?.roles) return Number.MAX_SAFE_INTEGER;
    return rolesData.roles.find((r) => r.role_name === myOrgRoleName)?.priority ?? Number.MAX_SAFE_INTEGER;
  }, [myOrgRoleName, rolesData]);
  // Only these roles can be assigned from this screen (for everyone,
  // including cross-org admins) — Owner and the rest are managed elsewhere.
  const assignableOrgRoles = useMemo(() => {
    const allowed = orgRoles.filter((role) => ASSIGNABLE_ORG_ROLE_NAMES.includes(role.role_name));
    return isCrossOrgAdmin ? allowed : allowed.filter((role) => canManageRole(role, myOrgRolePriority));
  }, [isCrossOrgAdmin, orgRoles, myOrgRolePriority]);
  // Assigning roles also requires organization:id:update on THIS org and a
  // role ranked at or above OrganizationOwner — Approvers and anything below
  // it in priority never get the Assign button.
  const ownerPriority = useMemo(
    () => rolesData?.roles?.find((r) => r.role_name === "OrganizationOwner")?.priority ?? Number.MAX_SAFE_INTEGER,
    [rolesData]
  );
  const isOrgOwnerOrAdmin = isOwner && (isCrossOrgAdmin || myOrgRolePriority <= ownerPriority);
  const assignRoleTooltip = !isOrgOwnerOrAdmin
    ? "Requires Owner role"
    : !canUpdateOrganizationById
      ? "Requires the organization:id:update permission"
      : !canCreateRoleAssignment
        ? "Requires the auth:role-assignment:create permission"
        : assignableOrgRoles.length === 0
          ? "You can only manage roles at or below your own"
          : "";
  const canAssignRole = isOrgOwnerOrAdmin && canUpdateOrganizationById && canCreateRoleAssignment && assignableOrgRoles.length > 0;

  const {
    data: assignmentsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetRoleAssignmentsQuery({ orgId, perPage: 200 }, { skip: !canReadRoleAssignment });

  const allRows = assignmentsData?.role_assignments || [];

  // Client-side search over the assignee (email/app id) and role label, then
  // client-side pagination of the filtered set.
  const filteredRows = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    if (!term) return allRows;
    return allRows.filter((row) => {
      const { primary } = getAssigneeDisplay(row);
      const { label } = resolveAssignmentRole(row, rolesById);
      return `${primary} ${label}`.toLowerCase().includes(term);
    });
  }, [allRows, searchInput, rolesById]);
  const nPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const page = Math.min(currentPage, nPages);
  const rows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns = [
    {
      field: "assignee",
      headerName: "Assignee",
      flex: 1.4,
      minWidth: 200,
      renderCell: ({ row }) => {
        // Show the secondary line only for apps ("Application"), not the raw user id.
        const { primary, secondary: rawSecondary } = getAssigneeDisplay(row);
        const secondary = row.is_app ? rawSecondary : null;
        return (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontSize: "13px", color: "#111827" }}>{primary}</div>
            {secondary && <div style={{ fontSize: "11px", color: "#9ca3af" }}>{secondary}</div>}
          </div>
        );
      },
    },
    {
      field: "role_id",
      headerName: "Role",
      width: 180,
      renderCell: ({ row }) => {
        const { label, isUnknown } = resolveAssignmentRole(row, rolesById);
        return isUnknown ? (
          <span style={{ fontSize: "12px", color: "#b45309", fontWeight: 600 }}>⚠ {label}</span>
        ) : (
          <span style={{ fontSize: "13px" }}>{label}</span>
        );
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 130,
      renderCell: ({ row }) => <StatusChip isActive={!!row.is_active} />,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: ({ row }) => {
        if (!row.is_active) return [];

        const rowRole = resolveAssignmentRole(row, rolesById).role;
        const outranksMe = !isCrossOrgAdmin && rowRole && !canManageRole(rowRole, myOrgRolePriority);
        const canDeactivate = isOwner && canUpdateRoleAssignment && !outranksMe;
        const tooltip = !isOwner
          ? "Requires Owner role"
          : !canUpdateRoleAssignment
            ? "Requires the auth:role-assignment:update permission"
            : outranksMe
              ? "You can only manage roles at or below your own"
              : "Deactivate";

        return [
          <GridActionsCellItem
            key="deactivate"
            icon={
              <Tooltip title={tooltip}>
                <span>
                  <PauseCircleOutlineOutlined fontSize="small" sx={{ color: canDeactivate ? "#6b7280" : "#d1d5db" }} />
                </span>
              </Tooltip>
            }
            label="Deactivate"
            disabled={!canDeactivate}
            onClick={() => setDeactivateAssignment(row)}
          />,
        ];
      },
    },
  ];

  return (
    <div style={{ padding: "24px 32px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <div>
          <h1 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 4px", color: "#111827" }}>Org Access</h1>
          <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
            Assign Approver access for this organization.
          </p>
        </div>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={canReadRoleAssignment ? "Refresh" : "Requires the auth:role-assignment:read permission"}>
            <span>
              <IconButton
                size="small"
                aria-label="Refresh"
                onClick={refetch}
                disabled={isFetching || !canReadRoleAssignment}
              >
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={assignRoleTooltip}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined />}
                onClick={() => setAssignOpen(true)}
                disabled={!canAssignRole}
                sx={{ textTransform: "none", backgroundColor: "#86BC25", "&:hover": { backgroundColor: "#76a621" } }}
              >
                Assign role
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {isError ? (
        <Alert severity="error" action={<Button size="small" onClick={refetch}>Retry</Button>}>
          Failed to load this organization&apos;s role assignments.
        </Alert>
      ) : isLoading ? (
        <Skeleton variant="rounded" height={320} />
      ) : allRows.length === 0 ? (
        <Box sx={{ textAlign: "center", padding: "40px 0", border: "1px dashed #e5e7eb", borderRadius: 2 }}>
          <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: 12 }}>No org access assignments yet.</p>
          {canAssignRole && (
            <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setAssignOpen(true)}>
              Assign role
            </Button>
          )}
        </Box>
      ) : (
        <>
          <TextField
            size="small"
            placeholder="Search by email or role…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setCurrentPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearchInput("");
                      setCurrentPage(1);
                    }}
                    sx={{ padding: "2px" }}
                  >
                    <CloseOutlined sx={{ fontSize: 16, color: "#9ca3af" }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 280, mb: 2, "& .MuiOutlinedInput-root": { fontSize: "13px", background: "#fff", borderRadius: "8px" } }}
          />
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              overflow: "hidden",
              // header (44) + rows (44 each) + borders; keeps a full page's height even with fewer rows (last page, search)
              height: 46 + 36 + Math.max(rows.length, PAGE_SIZE) * 44, // + 36 for the columns toolbar
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              loading={isFetching}
              rowHeight={44}
              columnHeaderHeight={44}
              disableVirtualization
              disableRowSelectionOnClick
              {...columnsToolbarProps}
              components={columnsToolbarComponents}
              hideFooter
              localeText={{ noRowsLabel: "No assignments match your search." }}
              sx={{
                border: "none",
                fontSize: "13px",
                "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F8FAFC", borderBottom: "1px solid #DCE3EA" },
                "& .MuiDataGrid-columnHeaderTitle": { color: "#111827", fontSize: "12px", fontWeight: 700 },
                // modalTOS.css styles .MuiDataGrid-* globally with !important (per-row and
                // root box-shadow, row margin) — "&&" raises specificity to override it here.
                "&&": { boxShadow: "none !important" },
                "&& .MuiDataGrid-row": {
                  boxShadow: "none !important",
                  borderRadius: "0 !important",
                  marginBottom: "0 !important",
                },
                "& .MuiDataGrid-cell": { boxShadow: "none", borderBottom: "1px solid #f1f3f5" },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#F8FAFC" },
              }}
            />
          </div>
          <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: "13px", color: "#6b7280" }}>
              {filteredRows.length} assignment{filteredRows.length === 1 ? "" : "s"}
              {nPages > 1 && ` · Page ${page} of ${nPages}`}
            </div>
            {nPages > 1 && <Pagination nPages={nPages} currentPage={page} setCurrentPage={setCurrentPage} />}
          </div>
        </>
      )}

      {orgRoles.length === 0 && (
        <Chip
          label="No organization-scoped roles are configured yet — contact your platform administrator."
          size="small"
          sx={{ mt: 2, fontSize: "11px", background: "#fef3c7", color: "#92400e" }}
        />
      )}

      <OrgAssignRoleDrawer
        open={assignOpen}
        orgId={orgId}
        orgName={orgData?.name}
        orgRoles={assignableOrgRoles}
        onClose={() => setAssignOpen(false)}
        onCreated={() => {
          setAssignOpen(false);
          refetch();
        }}
      />

      <DeactivateOrgAssignmentDialog assignment={deactivateAssignment} onClose={() => setDeactivateAssignment(null)} />
    </div>
  );
};

export default OrgAccessPage;
